import { Effect, Actions, ofType, createEffect } from '@ngrx/effects';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { 
    ShoppingCartActions,
    ShoppingCartActionTypes, 
    LoadShoppingCartSuccess, 
    LoadShoppingCartFailure,
    CreateShoppingCartSuccess, 
    CreateShoppingCartFailure, 
    DeleteShoppingCartSuccess, 
    DeleteShoppingCartFailure,
    UpdateShoppingCartSuccess,
    UpdateShoppingCartFailure,
    CloneShoppingCartSuccess,
    CloneShoppingCartFailure,
    SelectShoppingCartNoRedirectSuccess,
    SelectShoppingCartNoRedirectFailure,
    SelectShoppingCartSuccess,
    SelectShoppingCartFailure,
    LoadShoppingCartItemSuccess,
    LoadShoppingCartItemFailure,
    UpdateShoppingCartItemQtyFailure,
    UpdateShoppingCartItemQtySuccess,
    DeleteShoppingCartItemFailure,
    DeleteShoppingCartItemSuccess,
    InsertShoppingCartItemFailure,
    InsertShoppingCartItemSuccess,
    ShareShoppingCartSuccess,
    ShareShoppingCartFailure,
    CheckoutShoppingCartFailure,
    CheckoutShoppingCartSuccess,
    DeleteShoppingCartItem,
    InsertShoppingCartItem 
} from '../actions/shoppingcart.actions';
import { map, exhaustMap, catchError, switchMap, delay, mergeMap } from 'rxjs/operators';
import { ShoppingCartService } from '../services/shopping-cart.service';
import { of } from 'rxjs';
import { DatePipe } from '@angular/common';
import { CommonService } from '../services';
import { Router } from '@angular/router';
import { ShoppingCart } from '../models/shopping-cart.model';
import { CartResponse, ApiResponse } from '../models/api-response.model';
import { CheckOutService } from '../services/checkout.service';
import { OrderConfirmationViewModel } from '../models/check-out.model';
import { NgProgressService } from '../services/ngProgress.service';

@Injectable()
export class ShoppingCartEffects {

    constructor(
        private actions$: Actions,
        private commonService: CommonService,
        private ngProgress: NgProgressService,
        private shoppingCartService: ShoppingCartService,
        private router: Router,
        private checkOutService: CheckOutService,
    ) {

    }

    @Effect()
    loadShoppingCart$: Observable<ShoppingCartActions> = this.actions$.pipe(
        ofType(ShoppingCartActionTypes.LoadShoppingCart),
        exhaustMap(() => this.shoppingCartService.getShoppingCart().pipe(
            map(shoppingcart => new LoadShoppingCartSuccess(shoppingcart)),
            catchError((error) => of(new LoadShoppingCartFailure(error)))
        ))
    );

    @Effect()
    createShoppingCart$: Observable<ShoppingCartActions> = this.actions$.pipe(
        ofType(ShoppingCartActionTypes.CreateShoppingCart),
        exhaustMap((action: any) => {
            let cartName;
            if (action.payload === null) {
                cartName = new DatePipe('en-US').transform(Date.now(), 'yyyyMMdd').toString() + this.commonService.random(100000, 199999).toString();
            } else {
                cartName = action.payload;
            }
            this.ngProgress.start();
            return this.shoppingCartService.createNewShoppingCart(cartName).pipe(
                map((cart: ShoppingCart) => {
                    this.ngProgress.done();
                    this.commonService.createCookie('CartId', cart.cartId.toString(), 7);
                    return new CreateShoppingCartSuccess(cart);
                }),
                catchError((error) => {
                    this.ngProgress.done();
                    this.commonService.implementToast('Cart already exists', 'Error');
                    return of(new CreateShoppingCartFailure('Cart already exists'));
                }),

            );
        })
    );

    @Effect()
    deleteShoppingCart$: Observable<ShoppingCartActions> = this.actions$.pipe(
        ofType(ShoppingCartActionTypes.DeleteShoppingCart),
        exhaustMap((action: any) => this.shoppingCartService.deleteShoppingCart(action.payload)
            .pipe(
                map((deleted) => {
                    if (deleted) {
                        return new DeleteShoppingCartSuccess(action.payload);
                    } else {
                        // this.commonService.showError('#delete-icon', 'Fail to delete cart', 'right');
                        this.commonService.implementToast('Fail to delete cart', 'Error');
                        return new DeleteShoppingCartFailure();
                    }
                }),

            )
        )
    );

    @Effect()
    updateShoppingCart$: Observable<ShoppingCartActions> = this.actions$.pipe(
        ofType(ShoppingCartActionTypes.UpdateShoppingCart),
        exhaustMap((action: any) => this.shoppingCartService.updateCartName(action.payload.cartName, action.payload.cartId)
            .pipe(
                map(
                    (response: any) => {
                        if (response.status) {
                            return new UpdateShoppingCartSuccess({ cartId: action.payload.cartId, cartName: action.payload.cartName });
                        } else {
                            return new UpdateShoppingCartFailure();
                        }
                    }
                )
            )
        )
    );

    @Effect()
    cloneShoppingCart$: Observable<ShoppingCartActions> = this.actions$.pipe(
        ofType(ShoppingCartActionTypes.CloneShoppintCart),
        exhaustMap((action: any) => this.shoppingCartService.DuplicateNewCart(action.payload).pipe(
            map((cart: ShoppingCart) => { cart.isSelected = true; return new CloneShoppingCartSuccess(cart); }),
            catchError(() => of(new CloneShoppingCartFailure()))
        )
        )
    );

    @Effect()
    selectShoppingCartNoDirect$: Observable<ShoppingCartActions> = this.actions$.pipe(
        ofType(ShoppingCartActionTypes.SelectShoppingCartNoRedirect),
        exhaustMap((action: any) => {
            return this.shoppingCartService.SetActiveCart(action.payload).pipe(
                map((res: any) => {
                    if (res.status) {
                        return new SelectShoppingCartNoRedirectSuccess(action.payload);
                    } else {
                        return new SelectShoppingCartNoRedirectFailure();
                    }
                })
            );
        })
    );

    @Effect()
    selectShoppingCart$: Observable<ShoppingCartActions> = this.actions$.pipe(
        ofType(ShoppingCartActionTypes.SelectShoppingCart),
        exhaustMap((action: any) => {
            return this.shoppingCartService.SetActiveCart(action.payload.cartId).pipe(
                map((res: any) => {
                    if (res.status) {
                        this.router.navigateByUrl('/myCarts/cart/' + action.payload.cartId + '/' + action.payload.cartName); return new SelectShoppingCartSuccess(action.payload);

                    } else {
                        return new SelectShoppingCartFailure();
                    }
                })
            );
        })
    );

    @Effect()
    loadShoppingCartItem$: Observable<ShoppingCartActions> = this.actions$.pipe(
        ofType(ShoppingCartActionTypes.LoadShoppingCartItem),
        exhaustMap((action: any) => {
            const shippingMethod = action.payload?.shippingMethod?.toLocaleLowerCase() == 'delivery' ? '' : action.payload?.shippingMethod;
            return this.shoppingCartService.getAllItemsFromShoppingCart(action.payload?.cartId, true, shippingMethod).pipe(
                map(ci => new LoadShoppingCartItemSuccess({ cartId: action.payload?.cartId, items: ci })),
                catchError(error => of(new LoadShoppingCartItemFailure()))
            );
        })
    );

    @Effect()
    updateShoppingCartItemQty$: Observable<ShoppingCartActions> = this.actions$.pipe(
        ofType(ShoppingCartActionTypes.UpdateShoppingCartItemQty),
        switchMap((action: any) => this.shoppingCartService.GetItemStatusQuantity(action.payload.cartID, action.payload.cartItemID, action.payload.stockCode, action.payload.quantity)
            .pipe(
                switchMap((res: CartResponse) => {
                    if (res.result === 'Success') {
                        if (!res.boolResult && res.message) {
                            this.commonService.implementToast(res.message,"Error");
                            // this.commonService.showError('.qtyInput' + action.payload.cartItemID, res.message, 'right');
                            return of(new UpdateShoppingCartItemQtyFailure()).pipe(delay(3000));
                        } else if (res.boolResult) {
                          if (res.message) { this.commonService.implementToast(res.message,"Error"); }
                          // if (res.message) { this.commonService.showError('.qtyInput' + action.payload.cartItemID, res.message, 'right'); }
                            // return of(new UpdateShoppingCartItemQtyFailure());
                            return this.shoppingCartService.UpdateItemQuantity(action.payload).pipe(
                                map((res: CartResponse) => {
                                    if (res.result === 'Success') {
                                        action.payload.subTotal = action.payload.quantity * action.payload.unitCost;
                                        return new UpdateShoppingCartItemQtySuccess(action.payload);
                                    } else {
                                      this.commonService.implementToast( res.message, 'Info');
                                     // this.commonService.showResult('.qtyInput' + action.payload.cartItemID, res.message, 'right');
                                        return new UpdateShoppingCartItemQtyFailure();
                                    }
                                })

                            );
                        } else if (res.stockStatus === 'o' && this.commonService.toNumber(res.qty) > 0 && this.commonService.toNumber(res.qty) < this.commonService.toNumber(action.payload.quantity)) {
                            this.commonService.showError('.qtyInput' + action.payload.cartItemID, 'We only have ' + res.qty + ' of this obsolete part left. Please adjust your ordered quantity.', 'right');
                            return of(new UpdateShoppingCartItemQtyFailure()).pipe(delay(3000));
                        } else {
                            return of(new UpdateShoppingCartItemQtyFailure()).pipe(delay(3000));
                        }
                    } else {
                        return of(new UpdateShoppingCartItemQtyFailure()).pipe(delay(3000));
                    }
                })
            )
        ),

    );

    deleteShoppingCartItem$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(ShoppingCartActionTypes.DeleteShoppingCartItem),
            mergeMap((action: DeleteShoppingCartItem) => {
                if (action?.payload?.stockCode.toLocaleLowerCase() === 'donation') {
                    return this.checkOutService.UpsertDonation(this.commonService.toNumber(action.payload.cartID), 0).pipe(
                        map(() => new DeleteShoppingCartItemSuccess(action.payload)),
                        catchError((error) => {
                            this.commonService.implementToast(error, 'Error');
                            return of(new DeleteShoppingCartItemFailure());
                        }),
                    );
                } else {
                    return this.shoppingCartService.deleteItemFromShoppingCart(action.payload.cartItemID).pipe(
                        map((res: ApiResponse) => {
                            if (res.status) {
                                return new DeleteShoppingCartItemSuccess(action.payload);
                            } else {
                                // this.commonService.showError('#' + action.payload.cartItemID, res.responseMessage, 'right');
                                this.commonService.implementToast(res.responseMessage, 'Error');
                                return new DeleteShoppingCartItemFailure();
                            }
                        })
                    );
                }
            })
        );
    });

    insertShoppingCartItem$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(ShoppingCartActionTypes.InsertShoppingCartItem),
            mergeMap((action: InsertShoppingCartItem) => {
                return this.shoppingCartService.addItemToShoppingCart(action.payload.stockCode, action.payload.cartId, action.payload.Qty).pipe(
                    map((data: any) => {
                        if (data.status) {
                            // this.commonService.implementToast(action.payload.stockCode + ' ' + data.responseMessage, 'Success');
                            return new InsertShoppingCartItemSuccess(data.cart);
                        } else {
                            this.commonService.implementToast(action.payload.stockCode + ' ' + data.responseMessage.split('<br />').join('\n'), 'Error');
                            return new InsertShoppingCartItemFailure();
                        }
                    })
                );
            })
        );
    });

    @Effect()
    shareShoppingCart$ = this.actions$.pipe(
        ofType(ShoppingCartActionTypes.ShareShoppingCart),
        mergeMap((action: any) => this.shoppingCartService.shareCartToggle(action.payload).pipe(
            map((res: number) => new ShareShoppingCartSuccess({ cartId: action.payload, status: res })),
            catchError(error => of(new ShareShoppingCartFailure()))
        ))
    );

    @Effect()
    checkoutShoppingCart$ = this.actions$.pipe(
        ofType(ShoppingCartActionTypes.CheckoutShoppingCart),
        switchMap((action: any) => this.checkOutService.PlaceOrder(action.payload.cartId, '').pipe(
            map((res: OrderConfirmationViewModel) => {
                if (res.apiResponse.status) {
                    const url = `/myCarts/cart/${action.payload.cartId}/${action.payload.cartName}/order-confirmation`;
                    this.router.navigateByUrl(url);
                    return new CheckoutShoppingCartSuccess(action.payload.cartId);
                } else {
                    this.commonService.showError('#next-button', res.apiResponse.responseMessage, 'right');
                    return new CheckoutShoppingCartFailure();
                }
            }),
            catchError(error => of(new CheckoutShoppingCartFailure()))
        )
        )
    );
}
